export type TransportModeKind =
    | "pickup"
    | "ground"
    | "flight_nanny"
    | "air_cargo";

export type TransportDetail =
    | {
          kind: "pickup";
          city: string;
          meetsHalfway: boolean;
          meetups?: string[];
      }
    | {
          kind: "ground";
          states: string[];
          feeLabel?: string;
          coversSeeker: boolean;

          seekerState?: string | null;
      }
    | { kind: "flight_nanny" };

export type TransportMode = {
    kind: TransportModeKind;
    label: string;

    summary: string;
    available: boolean;

    detail?: TransportDetail;
};

export type TransportModel = { modes: TransportMode[]; hasAny: boolean };

export type TransportStateChip = { code: string; yours: boolean };

export type TransportRow = {
    kind: TransportModeKind;
    title: string;
    subtitle?: string;
    stateChips?: TransportStateChip[];
};

export type TransportModuleView = {
    map?: { src?: string; chipLabel?: string };
    hero?: TransportRow & { recommended?: boolean; meetups?: string[] };
    alts: TransportRow[];
    emptyLine?: string;
};

type RawTransport = { type: string | null; label: string | null };

export type TransportInput = {
    transports: RawTransport[];
    isGroundTransportAvailable?: boolean | null;
    isFlightNannyTransportAvailable?: boolean | null;
    isAirCargoTransportAvailable?: boolean | null;

    seekerState?: string | null;
};

const STATE_CODE: Record<string, string> = {
    alabama: "AL",
    alaska: "AK",
    arizona: "AZ",
    arkansas: "AR",
    california: "CA",
    colorado: "CO",
    connecticut: "CT",
    delaware: "DE",
    "district of columbia": "DC",
    florida: "FL",
    georgia: "GA",
    hawaii: "HI",
    idaho: "ID",
    illinois: "IL",
    indiana: "IN",
    iowa: "IA",
    kansas: "KS",
    kentucky: "KY",
    louisiana: "LA",
    maine: "ME",
    maryland: "MD",
    massachusetts: "MA",
    michigan: "MI",
    minnesota: "MN",
    mississippi: "MS",
    missouri: "MO",
    montana: "MT",
    nebraska: "NE",
    nevada: "NV",
    "new hampshire": "NH",
    "new jersey": "NJ",
    "new mexico": "NM",
    "new york": "NY",
    "north carolina": "NC",
    "north dakota": "ND",
    ohio: "OH",
    oklahoma: "OK",
    oregon: "OR",
    pennsylvania: "PA",
    "rhode island": "RI",
    "south carolina": "SC",
    "south dakota": "SD",
    tennessee: "TN",
    texas: "TX",
    utah: "UT",
    vermont: "VT",
    virginia: "VA",
    washington: "WA",
    "west virginia": "WV",
    wisconsin: "WI",
    wyoming: "WY",
};
const VALID_CODES = new Set(Object.values(STATE_CODE));

const STATE_NAME_PATTERNS: ReadonlyArray<readonly [RegExp, string]> =
    Object.keys(STATE_CODE)
        .sort((a, b) => b.length - a.length)
        .map(
            (name) => [new RegExp(`\\b${name}\\b`), STATE_CODE[name]] as const,
        );

function stateFromLabel(
    label: string,
): { code: string; explicit: boolean } | null {
    const code = label.match(/\bin\s+([A-Z]{2})\b/);
    if (code && VALID_CODES.has(code[1]))
        return { code: code[1], explicit: true };
    const lower = label.toLowerCase();
    for (const [re, code] of STATE_NAME_PATTERNS) {
        if (re.test(lower)) return { code, explicit: false };
    }
    return null;
}

function feeFromLabels(labels: string[]): string | undefined {
    let min: number | undefined;
    for (const l of labels) {
        for (const m of l.matchAll(/\$(\d[\d,]*)/g)) {
            const at = m.index ?? 0;

            const after = l
                .slice(at + m[0].length, at + m[0].length + 10)
                .toLowerCase();
            if (after.includes("deposit")) continue;
            const n = Number(m[1].replace(/,/g, ""));
            if (Number.isFinite(n) && (min === undefined || n < min)) min = n;
        }
    }
    return min === undefined ? undefined : `from $${min.toLocaleString()}`;
}

function pickupCity(label: string): string {
    const m = label.match(/pick ?up\s+(?:in|at)\s+(.+?)(?:\s+or\b|[.;]|$)/i);
    return m ? m[1].trim() : label.replace(/[.;]\s*$/, "").trim();
}

export function availableOnly(model: TransportModel): TransportModel {
    return { ...model, modes: model.modes.filter((m) => m.available) };
}

export function buildTransportModel(input: TransportInput): TransportModel {
    const rows = input.transports ?? [];
    const pickups = rows.filter((t) => t.type === "pickup" && t.label);
    const deliveries = rows.filter((t) => t.type === "delivery" && t.label);

    const modes: TransportMode[] = [];

    if (pickups.length > 0) {
        const city = pickupCity(pickups[0].label ?? "");
        const meetsHalfway = pickups.some((p) =>
            /meet.*halfway/i.test(p.label ?? ""),
        );
        modes.push({
            kind: "pickup",
            label: "Pickup",
            summary: city,
            available: true,
            detail: { kind: "pickup", city, meetsHalfway },
        });
    }

    const resolved = deliveries
        .map((d) => stateFromLabel(d.label ?? ""))
        .filter((r): r is { code: string; explicit: boolean } => r !== null);

    const states = [...new Set(resolved.map((r) => r.code))];
    const explicitStates = new Set(
        resolved.filter((r) => r.explicit).map((r) => r.code),
    );
    const groundFromRows = deliveries.length > 0;
    if (groundFromRows || input.isGroundTransportAvailable) {
        const feeLabel = feeFromLabels(deliveries.map((d) => d.label ?? ""));
        const coversSeeker = Boolean(
            input.seekerState && explicitStates.has(input.seekerState),
        );
        const reach =
            states.length > 0
                ? coversSeeker
                    ? `Reaches ${input.seekerState}`
                    : `Reaches ${states.length} ${states.length === 1 ? "state" : "states"}`
                : "Delivered to you";
        modes.push({
            kind: "ground",
            label: "Ground delivery",

            summary: groundFromRows
                ? [reach, feeLabel].filter(Boolean).join(" · ")
                : "Available",
            available: true,

            detail: {
                kind: "ground",
                states,
                feeLabel,
                coversSeeker,
                seekerState: coversSeeker ? (input.seekerState ?? null) : null,
            },
        });
    }

    modes.push({
        kind: "flight_nanny",
        label: "Flight nanny",
        summary: input.isFlightNannyTransportAvailable
            ? "Available"
            : "Not listed",
        available: Boolean(input.isFlightNannyTransportAvailable),
        detail: input.isFlightNannyTransportAvailable
            ? { kind: "flight_nanny" }
            : undefined,
    });
    modes.push({
        kind: "air_cargo",
        label: "Air cargo",
        summary: input.isAirCargoTransportAvailable
            ? "Available"
            : "Not listed",
        available: Boolean(input.isAirCargoTransportAvailable),
    });

    return { modes, hasAny: modes.some((m) => m.available) };
}

export const MODE_ORDER: readonly TransportModeKind[] = [
    "pickup",
    "ground",
    "flight_nanny",
    "air_cargo",
];

const NEARBY_MILES = 60;

export function toTransportModuleView(
    model: TransportModel,
    ctx: {
        mapSrc?: string;
        city?: string;
        distanceLabel?: string;
        distanceMiles?: number | null;
    },
): TransportModuleView {
    const chipLabel =
        [ctx.city, ctx.distanceLabel].filter(Boolean).join(" · ") || undefined;
    const map =
        ctx.mapSrc || chipLabel ? { src: ctx.mapSrc, chipLabel } : undefined;

    const available = availableOnly(model).modes;
    if (available.length === 0) {
        return {
            map,
            alts: [],
            emptyLine: "Not nearby? Ask Kits about delivery options.",
        };
    }

    const modeByKind = new Map(
        available.map((mode) => [mode.kind, mode] as const),
    );
    const near = ctx.distanceMiles != null && ctx.distanceMiles <= NEARBY_MILES;
    const hero = pickHero(modeByKind, available, ctx.distanceMiles, near);
    const alts = MODE_ORDER.filter((kind) => kind !== hero.kind)
        .map((kind) => modeByKind.get(kind))
        .filter((mode): mode is TransportMode => mode !== undefined)
        .map((mode) => ({
            kind: mode.kind,
            title: mode.label,
            subtitle: mode.summary,
            stateChips: groundChips(mode),
        }));

    return {
        map,
        hero: {
            kind: hero.kind,
            title: hero.label,
            subtitle: heroSubtitle(hero),
            recommended: isRecommended(hero, near, alts.length > 0),
            meetups: pickupMeetups(hero),
            stateChips: groundChips(hero),
        },
        alts,
    };
}

function pickHero(
    modeByKind: Map<TransportModeKind, TransportMode>,
    available: TransportMode[],
    distanceMiles: number | null | undefined,
    near: boolean,
): TransportMode {
    const pickup = modeByKind.get("pickup");
    if (pickup && (distanceMiles == null || near)) return pickup;
    return modeByKind.get("ground") ?? available[0];
}

function heroSubtitle(hero: TransportMode): string {
    if (hero.kind === "pickup") {
        const detail = hero.detail?.kind === "pickup" ? hero.detail : undefined;
        if (detail?.meetups?.length) return "Meetups listed";
        return detail?.meetsHalfway ? "Halfway possible" : "Meet the dog";
    }
    if (hero.kind === "flight_nanny") return "A nanny flies with them.";
    return hero.summary;
}

function isRecommended(
    hero: TransportMode,
    near: boolean,
    hasAlts: boolean,
): boolean {
    if (!hasAlts) return false;
    if (hero.kind === "pickup") return near;
    if (hero.kind === "ground" && hero.detail?.kind === "ground") {
        return hero.detail.coversSeeker;
    }
    return false;
}

function pickupMeetups(hero: TransportMode): string[] | undefined {
    return hero.kind === "pickup" && hero.detail?.kind === "pickup"
        ? hero.detail.meetups
        : undefined;
}

function groundChips(mode: TransportMode): TransportStateChip[] | undefined {
    if (mode.detail?.kind !== "ground" || mode.detail.states.length === 0) {
        return undefined;
    }
    const { states, seekerState } = mode.detail;
    return states
        .map((code) => ({ code, yours: code === seekerState }))
        .sort((a, b) => Number(b.yours) - Number(a.yours));
}
